/**
 * Display sound effects. The spinner tick is synthesized with WebAudio; the rest
 * play real audio files (fetched + decoded on `unlock()`), falling back to the
 * synthesized version of the effect if a file is not loaded.
 * Browsers only allow audio after a user gesture: call `unlock()` from a click.
 * Every effect is a silent no-op until then (or where WebAudio is missing).
 */

export type FileEffect = 'revealCorrect' | 'revealWrong' | 'airHorn' | 'steal' | 'champion';

export const EFFECT_URLS: Record<FileEffect, string> = {
  revealCorrect: '/sfx/reveal-correct.mp3',
  revealWrong: '/sfx/reveal-wrong.mp3',
  airHorn: '/sfx/airhorn.mp3',
  steal: '/sfx/steal.mp3',
  champion: '/sfx/champion.mp3',
};

/** Level for file-based effects, so they sit under the Spotify music (SDK volume 0.8). */
const FILE_GAIN = 0.6;

/** Whether an effect plays its decoded file or the synthesized fallback. */
export function pickSource(buffers: Partial<Record<FileEffect, unknown>>, name: FileEffect): 'file' | 'synth' {
  return buffers[name] ? 'file' : 'synth';
}

/** Fetch + decode every effect file in parallel; any failure just leaves that effect out. */
export async function loadBuffers(
  ac: Pick<BaseAudioContext, 'decodeAudioData'>,
  fetchFn: typeof fetch,
): Promise<Partial<Record<FileEffect, AudioBuffer>>> {
  const out: Partial<Record<FileEffect, AudioBuffer>> = {};
  await Promise.all(
    (Object.keys(EFFECT_URLS) as FileEffect[]).map(async (name) => {
      try {
        const res = await fetchFn(EFFECT_URLS[name]);
        if (!res.ok) return;
        out[name] = await ac.decodeAudioData(await res.arrayBuffer());
      } catch {
        /* synth fallback */
      }
    }),
  );
  return out;
}

/** Overall effects level; kept moderate so the music stays on top. */
const MASTER_GAIN = 0.28;

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let fileOut: GainNode | null = null;
let buffers: Partial<Record<FileEffect, AudioBuffer>> = {};

export function unlock(): void {
  if (typeof window === 'undefined' || typeof AudioContext === 'undefined') return;
  try {
    if (!ctx) {
      ctx = new AudioContext();
      master = ctx.createGain();
      master.gain.value = MASTER_GAIN;
      master.connect(ctx.destination);
      fileOut = ctx.createGain();
      fileOut.gain.value = FILE_GAIN;
      fileOut.connect(ctx.destination);
      if (typeof fetch !== 'undefined') {
        void loadBuffers(ctx, fetch.bind(globalThis)).then((b) => { buffers = b; }).catch(() => {});
      }
    }
    if (ctx.state === 'suspended') void ctx.resume();
  } catch {
    ctx = null;
    master = null;
    fileOut = null;
  }
}

/** Plays the effect's file if it decoded; returns false so the caller can synthesize instead. */
function playFile(name: FileEffect): boolean {
  const a = ready();
  if (!a || !fileOut || pickSource(buffers, name) !== 'file') return false;
  try {
    const src = a.ac.createBufferSource();
    src.buffer = buffers[name]!;
    src.connect(fileOut);
    src.start();
    return true;
  } catch {
    return false;
  }
}

function ready(): { ac: AudioContext; out: GainNode } | null {
  if (!ctx || !master || ctx.state !== 'running') return null;
  return { ac: ctx, out: master };
}

/** One oscillator note with a quick attack and exponential release. */
function tone(
  ac: AudioContext, out: AudioNode, type: OscillatorType,
  freq: number, start: number, dur: number, peak: number, endFreq?: number,
): void {
  const osc = ac.createOscillator();
  const g = ac.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, start);
  if (endFreq) osc.frequency.exponentialRampToValueAtTime(endFreq, start + dur);
  g.gain.setValueAtTime(0.0001, start);
  g.gain.exponentialRampToValueAtTime(peak, start + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  osc.connect(g).connect(out);
  osc.start(start);
  osc.stop(start + dur + 0.02);
}

/** A short click for each turn of the spinner. */
export function spinTick(): void {
  const a = ready();
  if (!a) return;
  tone(a.ac, a.out, 'square', 1800, a.ac.currentTime, 0.035, 0.25);
}

/** Rising major arpeggio when someone got it, a low descending buzz when nobody did. */
export function reveal(correct: boolean): void {
  if (playFile(correct ? 'revealCorrect' : 'revealWrong')) return;
  const a = ready();
  if (!a) return;
  const t = a.ac.currentTime;
  if (correct) {
    [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => tone(a.ac, a.out, 'triangle', f, t + i * 0.08, 0.35, 0.5));
  } else {
    tone(a.ac, a.out, 'sawtooth', 220, t, 0.25, 0.3, 160);
    tone(a.ac, a.out, 'sawtooth', 165, t + 0.22, 0.4, 0.3, 110);
  }
}

/** The classic stadium air horn: "baaa · ba · ba · baaaaa". */
export function airHorn(): void {
  if (playFile('airHorn')) return;
  airHornSynth();
}

/** Victory fanfare for the finale (falls back to the synthesized air horn). */
export function champion(): void {
  if (playFile('champion')) return;
  airHornSynth();
}

function airHornSynth(): void {
  const a = ready();
  if (!a) return;
  const { ac, out } = a;
  const filter = ac.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 2600;
  filter.Q.value = 2;
  filter.connect(out);
  const t0 = ac.currentTime;
  const blasts: [number, number][] = [[0, 0.42], [0.5, 0.14], [0.7, 0.14], [0.9, 1.1]];
  for (const [at, dur] of blasts) {
    const start = t0 + at;
    const g = ac.createGain();
    g.gain.setValueAtTime(0.0001, start);
    g.gain.exponentialRampToValueAtTime(0.45, start + 0.03);
    g.gain.setValueAtTime(0.45, start + dur - 0.06);
    g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
    g.connect(filter);
    // Detuned saws a semitone-ish apart give the horn its brassy beating.
    for (const f of [415, 440, 466, 830]) {
      const osc = ac.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(f * 0.97, start);
      osc.frequency.linearRampToValueAtTime(f, start + 0.05);
      osc.connect(g);
      osc.start(start);
      osc.stop(start + dur + 0.02);
    }
  }
}

/** A sneaky downward swoosh for a stolen square. */
export function steal(): void {
  if (playFile('steal')) return;
  const a = ready();
  if (!a) return;
  const { ac, out } = a;
  const t = ac.currentTime;
  const len = Math.floor(ac.sampleRate * 0.5);
  const buf = ac.createBuffer(1, len, ac.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  const noise = ac.createBufferSource();
  noise.buffer = buf;
  const band = ac.createBiquadFilter();
  band.type = 'bandpass';
  band.Q.value = 4;
  band.frequency.setValueAtTime(3000, t);
  band.frequency.exponentialRampToValueAtTime(300, t + 0.45);
  const g = ac.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.6, t + 0.05);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.48);
  noise.connect(band).connect(g).connect(out);
  noise.start(t);
  noise.stop(t + 0.5);
  tone(ac, out, 'square', 880, t + 0.05, 0.35, 0.15, 220);
}
